import React from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import LayoutContent from '../../components/AppLayout/LayoutContent';
import SquareIntegration from '../components/SquareIntegration';
import TaxList from '../components/TaxList';
import { Panel } from '../../components/PanelLayout';
import InfoCardList from '../../components/InfoCard/InfoCardList';
import InfoCardsSkeleton from '../../components/InfoCard/InfoCardColumnSkeleton';
import { ContextParams } from '../../types/ContextParams.interface';
import { selectCompanySettingsState } from '../redux/CompanySettingsSelectors';
import { getCompanyIntegrations, getSmbLocations, getTaxes } from '../redux/CompanySettingsActions';
import {
  CompanyResourceActions,
  PrincipalPermissions,
  ResourceType,
  SecurityScope,
  Verifier,
} from '@ready/security.core';
import { PosSystemType } from '@ready/dashboardv2api.contracts';
import { selectPermissions } from '../../redux/selectors/sessionSelectors/sessionSelectors';
import { pageErrorState } from '../../redux/actions/uiActions/responseStateActions';
import { CompanyLocationService } from '../../services/companyLocationsService';
import LocationsService from '../../locations/services/LocationsService';

const Integrations = () => {
  const { contextId, locationId } = useParams<ContextParams>();

  const dispatch = useDispatch();

  const {
    companyIntegrations: { loading, integrations },
    smbTaxes: { loading: taxesLoading, taxes, smbConfiguredLocations, smbLocationsLoading },
  } = useSelector(selectCompanySettingsState);

  React.useEffect(() => {
    dispatch(getSmbLocations(contextId));
    dispatch(getCompanyIntegrations(contextId));
  }, [contextId, dispatch]);

  React.useEffect(() => {
    if (smbConfiguredLocations.length > 0) {
      dispatch(getTaxes(contextId));
    }
  }, [contextId, dispatch, smbConfiguredLocations]);

  // No dedicated "does this company have a Square location" endpoint exists (unlike /smblocations
  // for basicpos), so this checks the company's locations client-side. Defaults to showing the
  // panel (matching prior behavior) until proven otherwise, so a failed check never hides it from
  // a real Square customer.
  const [hasSquareLocation, setHasSquareLocation] = React.useState(true);
  const [squareCheckLoading, setSquareCheckLoading] = React.useState(true);
  React.useEffect(() => {
    let cancelled = false;
    const checkForSquareLocation = async () => {
      try {
        setSquareCheckLoading(true);
        const locations = await CompanyLocationService.getCompanyLocations(contextId);
        const locationDetails = await Promise.all(
          locations.map((location) => LocationsService.getClientLocationById(contextId, location._id))
        );
        if (!cancelled) {
          setHasSquareLocation(locationDetails.some((location) => location.posSystemType === PosSystemType.square));
        }
      } catch {
        if (!cancelled) setHasSquareLocation(true);
      } finally {
        if (!cancelled) setSquareCheckLoading(false);
      }
    };
    checkForSquareLocation();
    return () => {
      cancelled = true;
    };
  }, [contextId]);

  const { permissionsList } = useSelector(selectPermissions);
  const hasPermissionAll = Verifier.check(
    new PrincipalPermissions(permissionsList),
    SecurityScope.company,
    ResourceType.company,
    CompanyResourceActions.all,
    locationId
  );
  const hasPermissionView = Verifier.check(
    new PrincipalPermissions(permissionsList),
    SecurityScope.company,
    ResourceType.company,
    CompanyResourceActions.view,
    locationId
  );

  if (!hasPermissionAll && !hasPermissionView) {
    dispatch(pageErrorState(401));
  }

  return (
    <LayoutContent title='Company Settings' containerType='within' loadingContent={loading}>
      {smbLocationsLoading ? (
        <Panel title='Taxes' fullContentArea>
          <InfoCardList>
            <InfoCardsSkeleton />
          </InfoCardList>
        </Panel>
      ) : (
        smbConfiguredLocations.length > 0 && <TaxList taxes={taxes} loading={taxesLoading} />
      )}
      {!squareCheckLoading && hasSquareLocation && (
        <SquareIntegration companyId={contextId} integrations={integrations} hasPermissionAll={hasPermissionAll} />
      )}
    </LayoutContent>
  );
};

export default Integrations;
